import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button, Select, SelectItem, Label, Input, Textarea, DateInput } from '../../../components/ui';
import { useFlexibleList } from '../../../hooks/useSWR';
import { PlusIcon, TrashIcon, ChevronDownIcon } from '../../../components/ui/Icons';

// ChevronUpIcon - simple SVG component
const ChevronUpIcon = ({ className = "h-5 w-5", ...props }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

/**
 * ComplaintActionsIntegration - Component for managing complaint actions within the complaint form
 * Similar to ApprovalIntegration but for ComplaintActions
 */
const ComplaintActionsIntegration = ({ 
  complaintId,
  formData,
  setFormData,
  config,
  isEditMode = false
}) => {
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [localActions, setLocalActions] = useState([]);

  // Fetch lookup data
  const { data: actionTypes = [], isLoading: actionTypesLoading } = useFlexibleList(
    config?.api?.lookupEndpoints?.complaintActionTypes ? config.api.lookupEndpoints.complaintActionTypes : null
  );

  // Fetch existing actions if complaintId exists
  const { data: existingActions = [] } = useFlexibleList(
    complaintId && isEditMode ? `/complaint-action/complaint/${complaintId}` : null
  );

  // Track if we've initialized to prevent infinite loops
  const isInitializedRef = React.useRef(false);
  const prevComplaintIdRef = React.useRef(complaintId);
  
  // Use ref to store latest actions for immediate access
  const localActionsRef = React.useRef([]);

  // Initialize actions only once when component mounts or complaintId changes
  useEffect(() => {
    // Reset initialization flag when complaintId changes
    if (prevComplaintIdRef.current !== complaintId) {
      isInitializedRef.current = false;
      prevComplaintIdRef.current = complaintId;
    }

    // Only initialize if we haven't already done so
    if (isInitializedRef.current) return;

    // Priority: existingActions (from API) > formData.complaintActions > empty array
    if (existingActions && Array.isArray(existingActions) && existingActions.length > 0) {
      // Load existing actions from API if available (for edit mode)
      setLocalActions(existingActions);
      isInitializedRef.current = true;
    } else {
      // For new records OR edit mode without existing actions, check formData first, then initialize empty array
      if (formData.complaintActions && Array.isArray(formData.complaintActions) && formData.complaintActions.length > 0) {
        setLocalActions(formData.complaintActions);
      } else {
        setLocalActions([]);
      }
      isInitializedRef.current = true;
    }
  }, [complaintId, isEditMode, existingActions, formData.complaintActions]);

  // Update ref whenever localActions changes
  useEffect(() => {
    localActionsRef.current = localActions;
  }, [localActions]);

  // Sync localActions to formData (but only if we've initialized)
  useEffect(() => {
    if (!isInitializedRef.current) return;

    // Sync immediately without timeout to ensure data is available when form is submitted
    const syncToFormData = () => {
      setFormData(prev => {
        // Only update if the data actually changed to prevent unnecessary re-renders
        const currentActions = prev.complaintActions || [];
        const currentActionsStr = JSON.stringify(currentActions);
        const localActionsStr = JSON.stringify(localActions);
        
        if (currentActionsStr === localActionsStr) {
          return prev;
        }
        
        // console.log('🔄 Syncing localActions to formData:', localActions);
        
        return {
          ...prev,
          complaintActions: localActions
        };
      });
    };
    
    syncToFormData();
  }, [localActions, setFormData]);
  
  // Force sync before form submission - expose sync function via window for debugging
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__syncComplaintActions = () => {
        // Always sync, even if not initialized - use current localActions state
        const latestActions = localActionsRef.current.length > 0 
          ? localActionsRef.current 
          : localActions;
        
        // console.log('🔄 Force syncing complaintActions to formData:', latestActions);
        // console.log('🔄 isInitialized:', isInitializedRef.current);
        // console.log('🔄 localActions length:', latestActions.length);
        
        // Use functional update to ensure we get the latest state
        setFormData(prev => {
          const updated = {
            ...prev,
            complaintActions: latestActions
          };
          // console.log('✅ Force sync complete - updated formData with complaintActions:', updated.complaintActions);
          // Update the ref as well to ensure consistency
          localActionsRef.current = latestActions;
          return updated;
        });
      };
      
      // Also expose getter for debugging and direct data access
      window.__getComplaintActions = () => {
        const latestActions = localActionsRef.current.length > 0 
          ? localActionsRef.current 
          : localActions;
        return {
          localActions: latestActions,
          localActionsState: localActions,
          isInitialized: isInitializedRef.current,
          formData: formData.complaintActions
        };
      };
    }
  }, [setFormData, formData, localActions]);

  const addAction = () => {
    const newAction = {
      id: `temp-${Date.now()}`, // Temporary ID for new actions
      actionTypeId: null,
      description: '',
      startedAt: null,
      responsible: '',
      dueDate: null,
      closeDate: null,
      isNew: true // Flag to identify new actions
    };
    setLocalActions(prev => {
      const updated = [...prev, newAction];
      // Update ref immediately for consistency
      localActionsRef.current = updated;
      return updated;
    });
    setIsExpanded(true); // Auto-expand when adding new action
  };

  const removeAction = (index) => {
    setLocalActions(prev => {
      const newActions = prev.filter((_, i) => i !== index);
      // Update ref immediately for consistency
      localActionsRef.current = newActions;
      return newActions;
    });
  };

  const updateAction = (index, field, value) => {
    setLocalActions(prev => {
      const newActions = [...prev];
      newActions[index] = {
        ...newActions[index],
        [field]: value
      };
      // Update ref immediately for consistency
      localActionsRef.current = newActions;
      return newActions;
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  return (
    <div className={cn("border rounded-lg", theme.border.light, theme.colors.background)}>
      {/* Accordion Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
          theme.text.primary
        )}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">📋</span>
          <span className="font-semibold">Corrective / Preventive Actions</span>
          {localActions.length > 0 && (
            <span className={cn(
              "px-2 py-0.5 rounded-full text-xs font-medium",
              theme.colors.primary,
              theme.text.inverse
            )}>
              {localActions.length}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="w-5 h-5" />
        ) : (
          <ChevronDownIcon className="w-5 h-5" />
        )}
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <div className={cn("p-4 border-t", theme.border.light)}>
          {localActions.length === 0 ? (
            <div className={cn("text-center py-8", theme.text.secondary)}>
              <p className="mb-4">No actions added yet.</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addAction}
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add First Action
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {localActions.map((action, index) => (
                <div
                  key={action.id || index}
                  className={cn(
                    "p-4 rounded-lg border space-y-4",
                    theme.border.subtle,
                    theme.colors.backgroundAlt
                  )}
                >
                  {/* Action Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={cn("text-sm font-medium", theme.text.secondary)}>
                        Action #{index + 1}
                      </span>
                      {action.closeDate && (
                        <span className={cn(
                          "px-2 py-0.5 rounded-full text-xs font-medium",
                          "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                        )}>
                          Closed
                        </span>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeAction(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Action Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    {/* Action Type */}
                    <div className="md:col-span-3">
                      <Label htmlFor={`actionType-${index}`}>
                        Action Type
                      </Label>
                      <Select
                        id={`actionType-${index}`}
                        value={action.actionTypeId?.toString() || ''}
                        onChange={(e) => updateAction(index, 'actionTypeId', e.target.value ? parseInt(e.target.value) : null)}
                        disabled={actionTypesLoading}
                      >
                        <SelectItem value="">Select action type</SelectItem>
                        {actionTypes?.map((type) => (
                          <SelectItem key={type.id} value={type.id.toString()}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </Select>
                      {actionTypesLoading && (
                        <div className={cn("text-xs mt-1", theme.text.secondary)}>
                          Loading action types...
                        </div>
                      )}
                    </div>

                    {/* Started At */}
                    <div className="md:col-span-3">
                      <Label htmlFor={`startedAt-${index}`}>
                        Started At
                      </Label>
                      <DateInput
                        id={`startedAt-${index}`}
                        value={formatDateForInput(action.startedAt)}
                        onChange={(e) => {
                          const dateValue = e.target.value ? new Date(e.target.value).toISOString() : null;
                          updateAction(index, 'startedAt', dateValue);
                        }}
                      />
                    </div>

                    {/* Responsible */}
                    <div className="md:col-span-3">
                      <Label htmlFor={`responsible-${index}`}>
                        Responsible
                      </Label>
                      <Input
                        id={`responsible-${index}`}
                        type="text"
                        value={action.responsible || ''}
                        onChange={(e) => updateAction(index, 'responsible', e.target.value)}
                        placeholder="Enter responsible person"
                      />
                    </div>

                    {/* Due Date */}
                    <div className="md:col-span-3">
                      <Label htmlFor={`dueDate-${index}`}>
                        Due Date
                      </Label>
                      <DateInput
                        id={`dueDate-${index}`}
                        value={formatDateForInput(action.dueDate)}
                        onChange={(e) => {
                          const dateValue = e.target.value ? new Date(e.target.value).toISOString() : null;
                          updateAction(index, 'dueDate', dateValue);
                        }}
                      />
                    </div>

                    {/* Close Date */}
                    <div className="md:col-span-3">
                      <Label htmlFor={`closeDate-${index}`}>
                        Close Date
                      </Label>
                      <DateInput
                        id={`closeDate-${index}`}
                        value={formatDateForInput(action.closeDate)}
                        onChange={(e) => {
                          const dateValue = e.target.value ? new Date(e.target.value).toISOString() : null;
                          updateAction(index, 'closeDate', dateValue);
                        }}
                      />
                    </div>

                    {/* Description - Full Width */}
                    <div className="md:col-span-12">
                      <Label htmlFor={`description-${index}`} required>
                        Description
                      </Label>
                      <Textarea
                        id={`description-${index}`}
                        value={action.description || ''}
                        onChange={(e) => updateAction(index, 'description', e.target.value)}
                        placeholder="Enter action description"
                        rows={2}
                        required
                      />
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Action Button */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addAction}
                className="w-full"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Another Action
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ComplaintActionsIntegration;

